import { useEffect, useState } from 'react'

/**
 * Theme and motion preferences.
 *
 * Dark is the default because this product is left open on a second monitor for hours. The
 * reduced-motion hook reads the OS setting and is respected everywhere that animates, including
 * the Bug Hunt, which stops its render loop entirely rather than merely animating less.
 */

export type Theme = 'dark' | 'light'

const STORAGE_KEY = 'devpulse:theme'
const CHANGE_EVENT = 'devpulse:theme-changed'

export function getTheme(): Theme {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY)
    if (saved === 'dark' || saved === 'light') return saved
  } catch {
    // Storage blocked; fall through to the OS preference.
  }

  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme)
}

export function setTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // Applies for this session regardless.
  }
  applyTheme(theme)
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

export function useTheme(): [Theme, (theme: Theme) => void] {
  const [theme, setThemeState] = useState<Theme>(getTheme)

  useEffect(() => {
    const onChange = (): void => setThemeState(getTheme())
    window.addEventListener(CHANGE_EVENT, onChange)
    return () => window.removeEventListener(CHANGE_EVENT, onChange)
  }, [])

  return [theme, setTheme]
}

/**
 * Whether the user has asked for less motion.
 *
 * Live, not read once: someone can change this in their OS while the page is open, and a product
 * that only honours it on reload is honouring it badly.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches),
  )

  useEffect(() => {
    // An environment without media queries (an old webview, a test) has no preference to follow.
    if (typeof window.matchMedia !== 'function') return
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = (): void => setReduced(media.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  return reduced
}
